import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { dispatch } from '@ngneat/effects';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { SubscriptionManager } from '@tmx/core';
import { TourItinerary, TourService, TourStopPoi } from '@tmx/modus-operandi';
import { filter } from 'rxjs';

import {
  createAllTasksCompleteTourItinerary,
  createAssetsTourItinerary,
  createInviteDriversTourItinerary,
  createMapSetTourItinerary,
  createRoutingProfileTourItinerary,
  createVehicleGroupTourItinerary,
  createWelcomeTourItinerary,
} from '../account-manager-tour';
import { ALK_ROLES } from '../models/alk-roles';
import { AMS_ROLES } from '../models/ams-roles';
import { FLEET_ROLES } from '../models/fleet-roles';
import { OnboardingTourNavigationState } from '../models/onboarding-tour-navigation-state';
import { PARTNER_ROLES } from '../models/partner-roles';
import {
  companySettingsSnapshot,
  isFleetPortalOnly,
} from '../repositories/app-settings/app-settings.repository';
import { initializeOnboardingTourChecklist } from '../repositories/onboarding-tour/onboarding-tour.actions';
import {
  deregisterOnboardingTourEffects,
  registerOnboardingTourEffects,
} from '../repositories/onboarding-tour/onboarding-tour.effects';
import { completeItineraryByName$ } from '../repositories/onboarding-tour/onboarding-tour.repository';
import { composeRoles } from '../utils/compose-roles';
import { BrazeEventsService } from './braze-events.service';

@Injectable({ providedIn: 'root' })
export class OnboardingTourService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly tourService = inject(TourService);
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly sm = new SubscriptionManager();

  initialize() {
    registerOnboardingTourEffects();
    this.registerItineraries();
    this.listenForNavigation();
    this.listenForTourCompletions();
    this.tourService.onBeforeStartItinerary = (itinerary) =>
      this.onAttemptToStartTour(itinerary);
  }

  reset() {
    deregisterOnboardingTourEffects();
    this.sm.unsubscribe();
  }

  ngOnDestroy() {
    this.reset();
  }

  private registerItineraries() {
    const { roles } = getCurrentAuthenticatedUser();
    const alkRoles = isFleetPortalOnly() ? new Set<string>() : ALK_ROLES;
    const partnerRoles = isFleetPortalOnly()
      ? new Set<string>()
      : PARTNER_ROLES;
    const amsRoles = isFleetPortalOnly() ? new Set<string>() : AMS_ROLES;
    const companySettings = companySettingsSnapshot();

    const hasAssetsRole = composeRoles(alkRoles, partnerRoles, amsRoles);
    const hasCfsRole = composeRoles(alkRoles, FLEET_ROLES);
    const showCfs = companySettings.isFleetPortalEnabled && hasCfsRole(roles);
    const hasVehicleGroupsPermissions =
      companySettings.activities.viewVehicleGroups;
    const hasRoutingProfilesPermissions =
      companySettings.isRoutingProfileEnabled &&
      companySettings.activities.accessRoutingProfiles;
    const hasMapSetsPermissions = companySettings.isMapsetsEnabled;

    const allItineraries = [
      { tour: createWelcomeTourItinerary(this.tourService) },
      {
        tour: createAssetsTourItinerary(this.tourService),
        show: hasAssetsRole(roles),
      },
      {
        tour: createVehicleGroupTourItinerary(this.tourService),
        show: showCfs && hasVehicleGroupsPermissions,
      },
      {
        tour: createRoutingProfileTourItinerary(this.tourService),
        show: showCfs && hasRoutingProfilesPermissions,
      },
      {
        tour: createMapSetTourItinerary(this.tourService),
        show: showCfs && hasMapSetsPermissions,
      },
      {
        tour: createInviteDriversTourItinerary(this.tourService),
        show: hasAssetsRole(roles),
      },
      { tour: createAllTasksCompleteTourItinerary(this.tourService) },
    ];

    const visibleItineraries = allItineraries
      .filter(({ show }) => show !== false)
      .map(({ tour }) => tour);

    this.tourService.register(visibleItineraries, (event) => {
      this.brazeEventsService.logBrazeCustomEvents(event);
    });

    dispatch(initializeOnboardingTourChecklist());

    const navigationState = window.history
      .state as OnboardingTourNavigationState;
    if (navigationState.startTour === true) {
      this.tourService.startTour();
    }
  }

  private listenForNavigation() {
    this.sm.manage = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        if (!this.tourService.tourStarted()) return;

        const state = history.state as OnboardingTourNavigationState;

        if (state.showAssetBlocker === true) {
          history.replaceState(
            { ...history.state, showAssetBlocker: null },
            ''
          );
          this.showAssetRequiredMessage();
        } else if (state.tourItineraryToStart ?? '') {
          const index = this.tourService
            .itineraries()
            .findIndex((it) => it.getName() === state.tourItineraryToStart);
          if (index > -1) {
            history.replaceState(
              { ...history.state, tourItineraryToStart: null },
              ''
            );
            this.tourService.gotoItinerary(index);
          }
        } else {
          const allItineraries = this.tourService.itineraries();

          const newItineraryIndex = allItineraries.findIndex(
            (itinerary) =>
              itinerary.associatedUrl !== undefined &&
              location.pathname.includes(itinerary.associatedUrl) &&
              itinerary.canActivate() &&
              !itinerary.isCompleted &&
              itinerary.progress !== 'skipped'
          );

          if (newItineraryIndex > -1) {
            this.tourService.gotoItinerary(newItineraryIndex);
          }
        }
      });
  }

  private onAttemptToStartTour(itineraryToStart: TourItinerary) {
    const isTryingToStartInviteTour =
      itineraryToStart.getName() ===
      translate('accountManagerTour.inviteDriversTour.itineraryTitle');

    if (isTryingToStartInviteTour) {
      const addAssetsTour = this.tourService
        .itineraries()
        .find(
          (it) =>
            it.getName() ===
            translate('accountManagerTour.assetsTour.itineraryTitle')
        );

      if (addAssetsTour && !addAssetsTour.isCompleted) {
        if (addAssetsTour.associatedUrl !== undefined) {
          const isAlreadyOnAssetsPage = this.router.url.includes(
            addAssetsTour.associatedUrl
          );

          if (isAlreadyOnAssetsPage) {
            this.showAssetRequiredMessage();
          } else {
            this.router.navigate([addAssetsTour.associatedUrl], {
              state: { showAssetBlocker: true },
            });
          }
        }
        return false;
      }
    }

    const url = itineraryToStart.associatedUrl;

    if (url === undefined) {
      return true;
    }

    const isAlreadyOnPage = this.router.url.includes(url);

    if (!isAlreadyOnPage) {
      this.router.navigate([url], {
        state: { tourItineraryToStart: itineraryToStart.getName() },
      });
      return false;
    }
    return true;
  }

  private showAssetRequiredMessage() {
    const addAssetTourIndex = this.tourService
      .itineraries()
      .findIndex(
        (it) =>
          it.getName() ===
          translate('accountManagerTour.assetsTour.itineraryTitle')
      );
    const blockerStop = new TourStopPoi(
      '[data-tour-poi=""]',
      translate('accountManagerTour.blockerStop.title'),
      translate('accountManagerTour.blockerStop.body'),
      {
        nextButton: {
          displayText: translate('accountManagerTour.blockerStop.gotIt'),
          listener: () => {
            this.tourService.gotoItinerary(addAssetTourIndex);
          },
        },
      }
    );
    const blockerItinerary = new TourItinerary(
      'Blocker',
      [blockerStop],
      undefined,
      false
    );
    blockerItinerary.activate();
  }

  private listenForTourCompletions() {
    this.sm.manage = completeItineraryByName$.subscribe((name) => {
      this.tourService.completeItineraryByName(name);

      const allTasksCompleteName = translate(
        'accountManagerTour.allTasksCompleteTour.itineraryTitle'
      );
      const allItineraries = this.tourService.itineraries();

      const taskItineraries = allItineraries.filter(
        (it) =>
          it.getName() !== allTasksCompleteName && it.showNameInTourProgress
      );

      const allTasksAreComplete =
        taskItineraries.length > 0 &&
        taskItineraries.every((it) => it.isCompleted);

      if (allTasksAreComplete) {
        this.tourService.setTourComplete();
      }
    });
  }
}
